import { Button, Card, DropdownMenu, IconButton } from '@humanforce/mango-ui-react';
import { isSupportedHolidayYear } from './publicHolidayRegions';
import type { PublicHolidayDto } from './publicHolidayTypes';

export interface PublicHolidayCalendarMonthProps {
  locale: string;
  month: Date;
  rows: PublicHolidayDto[];
  onMonthChange: (month: Date) => void;
  compact?: boolean;
  onCreate?: (date: string) => void;
  onEdit?: (row: PublicHolidayDto) => void;
}

const currentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const LONG_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const LONG_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatHolidayDate = (value: string) => {
  const date = new Date(`${value}T00:00:00`);
  return `${LONG_DAYS[date.getDay()]} ${date.getDate()} ${LONG_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const groupCalendarHolidays = (rows: PublicHolidayDto[]) => {
  const groups = new Map<string, PublicHolidayDto[]>();
  rows.forEach((row) => {
    const key = row.name.trim().toLocaleLowerCase();
    groups.set(key, [...(groups.get(key) ?? []), row]);
  });
  return Array.from(groups.values()).map((groupRows) => ({
    name: groupRows[0].name,
    rows: groupRows.sort((left, right) =>
      (left.jurisdictionCode ?? 'National').localeCompare(right.jurisdictionCode ?? 'National')),
  }));
};

export const PublicHolidayCalendarMonth = ({
  locale,
  month,
  rows,
  onMonthChange,
  compact = false,
  onCreate,
  onEdit,
}: PublicHolidayCalendarMonthProps) => {
  // Mango 0.74.0 has DatePicker for selection but no event/month calendar with per-day content.
  // This semantic table composes Mango Button, IconButton and DropdownMenu controls with Mango spacing and tokens.
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const days = Array.from({ length: 42 }, (_, index) => new Date(month.getFullYear(), month.getMonth(), index - offset + 1));
  const iso = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const monthLabel = month.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const renderHolidayGroup = (group: ReturnType<typeof groupCalendarHolidays>[number]) => {
    const jurisdictions = group.rows.map((row) => row.jurisdictionCode ?? 'National');
    const tooltip = group.rows.length > 1
      ? `${group.name} — ${jurisdictions.join(', ')}`
      : group.name;
    const trigger = <Button
      className="public-holiday-calendar__event-button"
      fullWidth
      tooltip={tooltip}
      variant="tertiary"
      size="small"
      onClick={group.rows.length === 1 && onEdit ? () => onEdit(group.rows[0]) : undefined}
    >
      <span className="public-holiday-calendar__event-label" title={tooltip}>{group.name}</span>
    </Button>;

    return <div className="public-holiday-calendar__event" key={group.rows[0].id}>
      {group.rows.length === 1 || !onEdit ? trigger : <DropdownMenu
        trigger={trigger}
        items={group.rows.map((holiday) => ({
          label: `Edit ${holiday.jurisdictionCode ?? 'National'}`,
          icon: 'edit',
          onClick: () => onEdit(holiday),
        }))}
      />}
    </div>;
  };

  return <Card><div className={`mgo-stack mgo-gap-m${compact ? ' public-holiday-calendar-container--compact' : ''}`}>
    <div className="mgo-split">
      <div className="mgo-cluster mgo-gap-s"><IconButton icon="back" label="Previous month" variant="tertiary" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))} /><span className="mgo-text-l mgo-weight-strong">{monthLabel}</span><IconButton icon="next" label="Next month" variant="tertiary" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))} /></div>
      <Button label="Today" variant="secondary" size="small" onClick={() => onMonthChange(currentMonth())} />
    </div>
    <table className="public-holiday-calendar"><caption className="sr-only">Public holidays for {monthLabel}</caption><thead><tr>{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => <th key={day} scope="col">{day}</th>)}</tr></thead>
      <tbody>{Array.from({ length: 6 }, (_, week) => <tr key={week}>{days.slice(week * 7, week * 7 + 7).map((day) => {
        const date = iso(day);
        const holidayGroups = groupCalendarHolidays(rows.filter((row) => row.holidayDate === date));
        const inMonth = day.getMonth() === month.getMonth();
        return <td key={date} className={inMonth ? '' : 'public-holiday-calendar__outside'}>
          <div className="public-holiday-calendar__day">
            <span className="public-holiday-calendar__date" aria-hidden="true">{day.getDate()}</span>
            {onCreate && inMonth && isSupportedHolidayYear(day.getFullYear()) ? <span className="public-holiday-calendar__add"><IconButton icon="add" label={`Add holiday on ${formatHolidayDate(date)}`} variant="tertiary" size="small" onClick={() => onCreate(date)} /></span> : null}
          </div>
          <div className="mgo-stack mgo-gap-xs">{holidayGroups.map(renderHolidayGroup)}</div>
        </td>;
      })}</tr>)}</tbody>
    </table>
  </div></Card>;
};
